/**
 * HunterScanModal — Ultra-Wide Photo Scanner.
 *
 * One button. Drop 1-20 photos. Get back a full defect ledger keyed to
 * NSPIRE + Section 8 HQS + Dallas City Code, plus an auto-drafted Packet 1
 * mobilization proposal. Skips the 12-step Field Mode wizard entirely so
 * Shannon can work outside the wizard when needed.
 */
import { useState, useRef, useCallback } from "react";

const BACKEND_URL = "";

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || "");
    const idx = result.indexOf(",");
    resolve(idx >= 0 ? result.slice(idx + 1) : result);
  };
  reader.onerror = () => reject(new Error("read_failed"));
  reader.readAsDataURL(file);
});

const severityColor = (sev) => ({
  life_safety: "#EF4444",
  critical: "#F97316",
  high: "#FBBF24",
  medium: "#5AC8D8",
  low: "#86EFAC",
  observation: "#9CA3AF",
}[sev] || "#9CA3AF");

const HunterScanModal = ({ open, onClose, defaultAddress = "", defaultUnit = "" }) => {
  const [address, setAddress] = useState(defaultAddress);
  const [unit, setUnit] = useState(defaultUnit);
  const [inspectionLens, setInspectionLens] = useState("all");
  const [files, setFiles] = useState([]);          // File[] chosen by user
  const [previews, setPreviews] = useState([]);    // dataUrl[]
  const [busy, setBusy] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  // Voice caption ingestion — SpeechRecognition Web API, matches the pattern
  // already used in FieldMode's photo caption modal.
  const [voiceNotes, setVoiceNotes] = useState("");
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const toggleListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Voice capture not supported on this browser."); return; }
    if (listening && recogRef.current) {
      try { recogRef.current.stop(); } catch { /* noop */ }
      recogRef.current = null;
      setListening(false);
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (e) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
      }
      if (final) setVoiceNotes((prev) => (prev ? prev + " " : "") + final.trim());
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    r.start();
    recogRef.current = r;
    setListening(true);
  }, [listening]);

  const reset = useCallback(() => {
    setFiles([]); setPreviews([]); setResult(null);
    setError(""); setBusy(false); setProgressMsg("");
    setVoiceNotes("");
    if (recogRef.current) { try { recogRef.current.stop(); } catch { /* noop */ } recogRef.current = null; }
    setListening(false);
  }, []);

  const close = useCallback(() => { reset(); onClose && onClose(); }, [reset, onClose]);

  const onFilesChosen = useCallback(async (list) => {
    const arr = Array.from(list || []).slice(0, 20);
    setFiles(arr);
    const p = await Promise.all(arr.map((f) => new Promise((r) => {
      const rd = new FileReader();
      rd.onload = () => r(String(rd.result || ""));
      rd.onerror = () => r("");
      rd.readAsDataURL(f);
    })));
    setPreviews(p);
    setResult(null);
    setError("");
  }, []);

  const runScan = useCallback(async () => {
    if (!files.length) { setError("Add at least one photo first."); return; }
    if (!address.trim()) { setError("Property address is required."); return; }
    setBusy(true); setError(""); setResult(null);
    setProgressMsg(`Encoding ${files.length} photo${files.length === 1 ? "" : "s"}…`);
    try {
      const photos_b64 = [];
      for (let i = 0; i < files.length; i++) {
        setProgressMsg(`Encoding photo ${i + 1} of ${files.length}…`);
        photos_b64.push(await readAsBase64(files[i]));
      }
      setProgressMsg("Running vision analysis across all photos…");
      const r = await fetch(`${BACKEND_URL}/api/mf/hunter/scan`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: address.trim(),
          unit: unit.trim(),
          photos_b64,
          inspection_lens: inspectionLens,
          voice_notes: voiceNotes.trim(),
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(body.detail || `Scan failed (${r.status}).`);
        setBusy(false); setProgressMsg("");
        return;
      }
      setResult(body);
      setProgressMsg("");
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [files, address, unit, inspectionLens, voiceNotes]);

  if (!open) return null;

  return (
    <div data-testid="hunter-scan-modal"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) close(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(8,13,28,0.90)",
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  zIndex: 9600, padding: 12, overflowY: "auto" }}>
      <div style={{ background: "#0b1220", border: "1px solid rgba(0,255,255,0.35)",
                    borderLeft: "4px solid #00FFFF", borderRadius: 12,
                    padding: "1.4rem 1.3rem", width: "100%", maxWidth: 720,
                    marginTop: 20, marginBottom: 20,
                    boxShadow: "0 30px 80px rgba(0,0,0,0.65)" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11, letterSpacing: "0.24em", fontWeight: 800 }}>
              &gt; HUNTER_MODE · ULTRA_WIDE_SCAN
            </div>
            <div style={{ color: "#F3F4F6", fontWeight: 800, fontSize: 20, marginTop: 4 }}>
              Drop Photos → Get the Full Defect Ledger
            </div>
            <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 4, maxWidth: 520 }}>
              No 12-step wizard. Vision reads every photo across NSPIRE, HQS, and
              Dallas City Code — findings auto-map to correction SKUs and drop
              straight into a Packet 1 draft.
            </div>
          </div>
          <button data-testid="hunter-close" onClick={close} disabled={busy}
                  style={{ background: "transparent", color: "#9CA3AF",
                           border: "1px solid #374151", borderRadius: 6,
                           padding: "4px 10px", cursor: busy ? "not-allowed" : "pointer",
                           fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        {/* Address / Unit */}
        {!result && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ color: "#94A3B8", fontSize: 11, marginBottom: 4, letterSpacing: "0.1em" }}>PROPERTY ADDRESS</div>
                <input data-testid="hunter-address" value={address} onChange={(e) => setAddress(e.target.value)}
                       placeholder="5960 Siltstone Lane, Fort Worth, TX 76137"
                       style={{ width: "100%", background: "#111827", color: "#F3F4F6",
                                border: "1px solid #374151", borderRadius: 6,
                                padding: "10px 12px", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ color: "#94A3B8", fontSize: 11, marginBottom: 4, letterSpacing: "0.1em" }}>UNIT</div>
                <input data-testid="hunter-unit" value={unit} onChange={(e) => setUnit(e.target.value)}
                       placeholder="932"
                       style={{ width: "100%", background: "#111827", color: "#F3F4F6",
                                border: "1px solid #374151", borderRadius: 6,
                                padding: "10px 12px", fontSize: 14 }} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ color: "#94A3B8", fontSize: 11, marginBottom: 4, letterSpacing: "0.1em" }}>INSPECTION LENS · LOCKED</div>
              <div data-testid="hunter-lens-locked"
                   style={{ width: "100%", background: "#111827", color: "#00FFFF",
                            border: "1px solid rgba(0,255,255,0.35)", borderRadius: 6,
                            padding: "10px 12px", fontSize: 13, fontWeight: 700,
                            letterSpacing: "0.05em", display: "flex",
                            justifyContent: "space-between", alignItems: "center" }}>
                <span>All three · NSPIRE + Section 8 HQS + Dallas City Code</span>
                <span style={{ fontSize: 10, letterSpacing: "0.18em", color: "#94A3B8" }}>🔒 LOCKED</span>
              </div>
            </div>

            {/* Voice caption — SpeechRecognition; runs while photos load */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ color: "#94A3B8", fontSize: 11, letterSpacing: "0.1em" }}>VOICE CAPTION · captured next to vision output</div>
                <button data-testid="hunter-voice-toggle" type="button" onClick={toggleListening}
                        style={{ background: listening ? "#FCA5A5" : "transparent",
                                 color: listening ? "#0F172A" : "#86EFAC",
                                 border: `1px solid ${listening ? "#FCA5A5" : "rgba(134,239,172,0.55)"}`,
                                 borderRadius: 999, padding: "3px 10px", fontSize: 11,
                                 fontWeight: 800, cursor: "pointer", letterSpacing: "0.08em" }}>
                  {listening ? "⏹ STOP" : "🎙 SPEAK"}
                </button>
              </div>
              <textarea data-testid="hunter-voice-input" value={voiceNotes}
                        onChange={(e) => setVoiceNotes(e.target.value)}
                        placeholder="Speak or type — e.g., 'HVAC intake soot, hairline crack west wall, GFCI missing at bathroom vanity.'"
                        rows={2}
                        style={{ width: "100%", background: "#111827", color: "#F3F4F6",
                                 border: `1px solid ${listening ? "#86EFAC" : "#374151"}`,
                                 borderRadius: 6, padding: "10px 12px", fontSize: 13,
                                 resize: "vertical", fontFamily: "inherit" }} />
            </div>

            {/* Ultra-Wide capture button */}
            <button data-testid="hunter-choose-photos" onClick={() => fileRef.current?.click()} disabled={busy}
                    style={{ width: "100%", padding: "22px 16px",
                             background: "linear-gradient(135deg, #00FFFF 0%, #5AC8D8 100%)",
                             color: "#0b1220", border: "none", borderRadius: 10,
                             fontWeight: 900, fontSize: 17, letterSpacing: "0.06em",
                             cursor: busy ? "not-allowed" : "pointer",
                             boxShadow: "0 8px 22px rgba(0,255,255,0.35)" }}>
              📸 ULTRA-WIDE CAPTURE · {files.length ? `${files.length} loaded` : "add up to 20 photos"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment"
                   onChange={(e) => onFilesChosen(e.target.files)}
                   data-testid="hunter-file-input"
                   style={{ display: "none" }} />

            {previews.length > 0 && (
              <div data-testid="hunter-previews" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                            gap: 6, marginTop: 12, marginBottom: 10 }}>
                {previews.map((p, i) => (
                  <img key={i} src={p} alt={`p${i}`}
                       style={{ width: "100%", height: 82, objectFit: "cover",
                                borderRadius: 6, border: "1px solid #374151" }} />
                ))}
              </div>
            )}

            <button data-testid="hunter-run-scan" onClick={runScan} disabled={busy || !files.length}
                    style={{ width: "100%", padding: "14px 16px", marginTop: 6,
                             background: busy || !files.length ? "#374151" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                             color: "#0b1220", border: "none", borderRadius: 8,
                             fontWeight: 800, fontSize: 15, letterSpacing: "0.05em",
                             cursor: busy || !files.length ? "not-allowed" : "pointer" }}>
              {busy ? (progressMsg || "Analyzing…") : "▶ RUN HUNTER SCAN"}
            </button>

            {error && (
              <div data-testid="hunter-error" style={{ color: "#FCA5A5", background: "rgba(239,68,68,0.10)",
                            border: "1px solid rgba(239,68,68,0.4)", borderRadius: 6,
                            padding: "8px 12px", marginTop: 10, fontSize: 13 }}>
                {error}
              </div>
            )}
          </>
        )}

        {/* Result view */}
        {result && (
          <ScanResult data={result} onReset={reset} onClose={close} />
        )}
      </div>
    </div>
  );
};

const ScanResult = ({ data, onReset, onClose }) => {
  const scanInitial = data.scan || {};
  const packetInitial = data.packet_1 || {};
  const [scan, setScan] = useState(scanInitial);
  const [packet, setPacket] = useState(packetInitial);
  const [negotiating, setNegotiating] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showNegotiate, setShowNegotiate] = useState(false);
  const [negotiateErr, setNegotiateErr] = useState("");

  const runNegotiate = useCallback(async () => {
    if (!feedback.trim()) { setNegotiateErr("Enter the client's feedback first."); return; }
    setNegotiating(true); setNegotiateErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/mf/hunter/scan/${scan.scan_id}/negotiate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_feedback: feedback.trim(), line_adjustments: [] }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setNegotiateErr(body.detail || `Negotiation failed (${r.status}).`);
      } else {
        setScan(body.revised_scan || scan);
        setPacket(body.revised_packet_1 || packet);
        setFeedback("");
        setShowNegotiate(false);
      }
    } catch (e) {
      setNegotiateErr(String(e.message || e));
    } finally {
      setNegotiating(false);
    }
  }, [feedback, scan, packet]);

  const counts = scan.counts || {};
  const findings = scan.findings || [];
  const skus = scan.sku_manifest || [];
  const pay = packet?.outputs?.payment_breakdown || {};

  return (
    <div data-testid="hunter-result">
      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
        {[
          { l: "TOTAL", v: counts.total || 0, c: "#00FFFF" },
          { l: "LIFE-SAFETY", v: counts.life_safety || 0, c: "#EF4444" },
          { l: "CRITICAL", v: counts.critical || 0, c: "#F97316" },
          { l: "LABOR HRS", v: scan.estimated_labor_hours || 0, c: "#86EFAC" },
        ].map((tile) => (
          <div key={tile.l} style={{ background: "#111827", border: "1px solid #1f2937",
                                     borderRadius: 8, padding: "10px 8px", textAlign: "center" }}>
            <div style={{ color: tile.c, fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 10, letterSpacing: "0.15em", fontWeight: 800 }}>{tile.l}</div>
            <div style={{ color: "#F3F4F6", fontSize: 22, fontWeight: 800, marginTop: 4 }}>{tile.v}</div>
          </div>
        ))}
      </div>

      {/* Findings list */}
      <div style={{ color: "#94A3B8", fontSize: 11, letterSpacing: "0.18em", fontWeight: 700, marginBottom: 6 }}>
        DEFECT LEDGER · {findings.length} FINDING{findings.length === 1 ? "" : "S"}
      </div>
      <div data-testid="hunter-findings" style={{ maxHeight: 340, overflowY: "auto",
                    border: "1px solid #1f2937", borderRadius: 8, padding: 4, marginBottom: 14 }}>
        {findings.length === 0 && (
          <div style={{ color: "#9CA3AF", padding: 14, textAlign: "center", fontSize: 13 }}>
            No defects surfaced. The unit reads as turnover-ready across all inspection lenses.
          </div>
        )}
        {findings.map((f) => (
          <div key={f.finding_id} data-testid={`hunter-finding-${f.finding_id}`}
               style={{ borderLeft: `3px solid ${severityColor(f.severity)}`,
                        background: "rgba(255,255,255,0.02)",
                        padding: "10px 12px", margin: 4, borderRadius: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 14, flex: 1 }}>
                {f.element}
                {f.priority_badge === "LIFE-SAFETY" && (
                  <span style={{ marginLeft: 8, background: "#EF4444", color: "#0b1220",
                                 padding: "1px 6px", borderRadius: 3, fontSize: 9,
                                 letterSpacing: "0.15em", fontWeight: 900 }}>LIFE-SAFETY</span>
                )}
              </div>
              <div style={{ color: severityColor(f.severity), fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10, letterSpacing: "0.1em", fontWeight: 800,
                            textTransform: "uppercase" }}>{f.severity}</div>
            </div>
            <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{f.description}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
              {f.zone && f.zone !== "unknown" && <Tag c="#5AC8D8">{f.zone}</Tag>}
              {f.nspire_code && <Tag c="#A78BFA">{f.nspire_code}</Tag>}
              {f.hqs_code && <Tag c="#00FFFF">{f.hqs_code}</Tag>}
              {f.city_code && <Tag c="#F59E0B">{f.city_code}</Tag>}
              <Tag c="#86EFAC">{f.trade_lane}</Tag>
              <Tag c="#94A3B8">{f.est_labor_hours}h</Tag>
              {f.photo_index > 0 && <Tag c="#94A3B8">photo #{f.photo_index}</Tag>}
            </div>
            {f.recommended_action && (
              <div style={{ color: "#D1D5DB", fontSize: 12, marginTop: 6, fontStyle: "italic" }}>
                → {f.recommended_action}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* SKU manifest */}
      {skus.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: "#94A3B8", fontSize: 11, letterSpacing: "0.18em", fontWeight: 700, marginBottom: 6 }}>
            AUTO-CORRECTION SKUs
          </div>
          <div data-testid="hunter-skus" style={{ background: "#111827", border: "1px solid #1f2937",
                        borderRadius: 8, padding: "10px 12px" }}>
            {skus.map((s) => (
              <div key={s.sku} style={{ display: "flex", justifyContent: "space-between",
                                        padding: "4px 0", fontSize: 12, color: "#D1D5DB" }}>
                <span>{s.description}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#5AC8D8" }}>
                  {s.sku} · qty {s.quantity}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Packet 1 draft summary */}
      {packet.packet_id && (
        <div data-testid="hunter-packet-1" style={{ background: "rgba(0,255,255,0.06)",
                      border: "1px solid rgba(0,255,255,0.35)",
                      borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ color: "#00FFFF", fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, marginBottom: 6 }}>
            PACKET 1 · MOBILIZATION DRAFT · {packet.packet_id}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, color: "#D1D5DB", fontSize: 13 }}>
            <div>Packet 1 Mobilization <b style={{ color: "#F3F4F6" }}>${(pay.packet_1_mobilization_usd || 0).toLocaleString()}</b></div>
            <div>Packet 2 Payoff <b style={{ color: "#F3F4F6" }}>${(pay.packet_2_payoff_usd || 0).toLocaleString()}</b></div>
            <div>Stabilization Fee <b style={{ color: "#F3F4F6" }}>${(pay.stabilization_fee_usd || 0).toLocaleString()}</b></div>
            <div>Capital Ceiling <b style={{ color: "#F3F4F6" }}>${(pay.capital_ceiling_usd || 0).toLocaleString()}</b></div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
        <button data-testid="hunter-scan-again" onClick={onReset}
                style={{ background: "transparent", color: "#00FFFF",
                         border: "1px solid #00FFFF66", borderRadius: 6,
                         padding: "10px 16px", fontWeight: 700, fontSize: 13,
                         cursor: "pointer", letterSpacing: "0.1em" }}>
          ↺ SCAN ANOTHER
        </button>
        <button data-testid="hunter-negotiate-toggle" onClick={() => setShowNegotiate((v) => !v)}
                style={{ background: "transparent", color: "#F59E0B",
                         border: "1px solid rgba(245,158,11,0.55)", borderRadius: 6,
                         padding: "10px 16px", fontWeight: 700, fontSize: 13,
                         cursor: "pointer", letterSpacing: "0.1em" }}>
          {showNegotiate ? "× CLOSE" : "⚖ HUNTER NEGOTIATE"}
        </button>
        <button data-testid="hunter-done" onClick={onClose}
                style={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                         color: "#0b1220", border: "none", borderRadius: 6,
                         padding: "10px 20px", fontWeight: 800, fontSize: 13,
                         cursor: "pointer", letterSpacing: "0.1em" }}>
          DONE
        </button>
      </div>

      {/* Hunter Mode negotiation loop */}
      {showNegotiate && (
        <div data-testid="hunter-negotiate-panel"
             style={{ marginTop: 14, background: "rgba(245,158,11,0.06)",
                      border: "1px solid rgba(245,158,11,0.35)",
                      borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ color: "#F59E0B", fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, marginBottom: 6 }}>
            HUNTER NEGOTIATE · reissues Packet 1 with the client&apos;s counter
          </div>
          <textarea data-testid="hunter-negotiate-input"
                    value={feedback} onChange={(e) => setFeedback(e.target.value)}
                    placeholder="e.g., 'Owner will not fund the moisture restoration this cycle — defer that finding and cut labor by 2 hours; keep everything else.'"
                    rows={3}
                    style={{ width: "100%", background: "#111827", color: "#F3F4F6",
                             border: "1px solid #374151", borderRadius: 6,
                             padding: "10px 12px", fontSize: 13, resize: "vertical",
                             fontFamily: "inherit" }} />
          {negotiateErr && (
            <div style={{ color: "#FCA5A5", fontSize: 12, marginTop: 6 }}>{negotiateErr}</div>
          )}
          <button data-testid="hunter-negotiate-run" onClick={runNegotiate} disabled={negotiating}
                  style={{ width: "100%", marginTop: 8, padding: "10px 16px",
                           background: negotiating ? "#374151" : "#F59E0B",
                           color: "#0b1220", border: "none", borderRadius: 6,
                           fontWeight: 800, fontSize: 13, letterSpacing: "0.1em",
                           cursor: negotiating ? "not-allowed" : "pointer" }}>
            {negotiating ? "RECALIBRATING…" : "▶ REISSUE PACKET 1"}
          </button>
        </div>
      )}
    </div>
  );
};

const Tag = ({ c, children }) => (
  <span style={{ color: c, background: `${c}20`, border: `1px solid ${c}55`,
                 padding: "1px 6px", borderRadius: 3, letterSpacing: "0.06em" }}>
    {children}
  </span>
);

export default HunterScanModal;
